<template>
  <div class="date-selector" v-click-outside="closeCalendar">
    <v-icon :color="'#DA4453'" :font-size="'2em'" class="icon-calendar">calendar</v-icon>
    <div class="container">
      <span class="label">{{ $t('context.labels.period') }}</span>
      <div @click="toggleCalendar" class="period">{{ displayedPeriod }}</div>
    </div>
    <div class="selector" v-if="showCalendar">
      <div @click="goToToday" class="today-anchor"></div>
      <div class="year-month">
        <v-icon @click="shiftMonthBy(-1)" class="chevron left" h="2rem">chevron-right</v-icon>
        <span>{{monthsNames[currentMonth.month - 1]}} {{currentMonth.year}}</span>
        <v-icon @click="shiftMonthBy(1)" class="chevron right" h="2rem">chevron-right</v-icon>
      </div>

      <div class="days-of-the-week">
        <div :key="`dayHeader#${index}`" class="cell header" v-for="(day, index) of days">{{day}}</div>
      </div>
      <div class="cells">
        <div :key="`emptyCells#${index}`" class="cell empty" v-for="index in emptyCells"></div>
        <div :class="cssClasses(day)"
             :key="`day#${day + emptyCells}`"
             @click="select(day)"
             class="cell"
             v-for="day in daysInMonth">
          {{day}}
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import { DateTime, Info } from 'luxon'

  const MODES = {
    WEEK: 0,
    DAY: 1
  }

  const days = Info.weekdays('short').map(day => day.charAt(0))
  const monthsNames = Info.months('long')

  export default {
    name: 'DateSelector',
    props: {
      value: {
        type: String,
        default: DateTime.local().toISODate()
      },

      mode: {
        type: Number,
        default: MODES.WEEK
      },

      disableToday: {
        type: Boolean,
        default: false
      }
    },

    data() {
      return {
        days,
        monthsNames,
        currentMonth: DateTime.fromISO(DateTime.local().toISODate()).set({ day: 1 }),
        showCalendar: false
      }
    },

    computed: {
      displayedPeriod() {
        if (this.mode === MODES.WEEK) return 'Semaine ' + DateTime.fromISO(this.value).weekNumber
        return DateTime.fromISO(this.value)
                       .setLocale('fr')
                       .toFormat('dd LLL yyyy')
      },

      now() {
        return DateTime.fromISO(DateTime.local().toISODate())
      },

      weekNb() {
        return this.value ? DateTime.fromISO(this.value).weekNumber : null
      },

      value$() {
        return this.value ? DateTime.fromISO(this.value) : null
      },

      previousMonth() {
        return this.currentMonth.minus({ months: 1 })
      },

      emptyCells() {
        return this.currentMonth.weekday - 1
      },

      daysInMonth() {
        return this.currentMonth.daysInMonth
      }
    },

    methods: {
      cssClasses(day) {
        const now = this.now
        return {
          selected: this.isSelected(day),
          today: this.isToday(day, now),
          disabled: this.isUnselectable(day)
        }
      },

      shiftMonthBy(shift) {
        this.currentMonth = this.currentMonth.plus({ months: shift })
      },

      isUnselectable(day) {
        return this.disableToday ?
               this.currentMonth.set({ day }).diffNow() > 0 || (this.now.day === day && this.now.month === this.currentMonth.month) :
               this.currentMonth.set({ day }).diffNow() > 0
      },

      isSelected(day) {
        const date = this.currentMonth.set({ day })
        if (this.mode === MODES.WEEK)
          return date.weekNumber === this.weekNb && date.year === DateTime.fromISO(this.value).year
        else return date.toISODate() === (this.value$ && this.value$.toISODate())
      },

      isToday(day, now) {
        return this.currentMonth.set({ day }).toISODate() === now.toISODate()
      },

      select(day) {
        this.$emit('select', this.currentMonth.set({ day }).toISODate())
      },

      goToToday() {
        this.currentMonth = this.now.set({ day: 1 })
      },

      toggleCalendar() {
        this.showCalendar = true
        this.currentMonth = DateTime.fromISO(this.value).set({ day: 1 })
      },

      closeCalendar() {
        this.showCalendar = false
      }
    }
  }
</script>

<style lang="scss" scoped>
  $s: 3.5rem;
  $color: var(--header-color);
  $text-color: var(--header-text-color);

  .date-selector {
    height: 100%;
    flex: 1;
    margin-right: 2rem;
    border-radius: 5px 5px 0 0;
    background-color: #FFFFFF;
    max-height: 80px;
    padding: 1rem;
    position: relative;
    @include display-flex(center, flex-start, row);

    .icon-calendar {
      margin-right: 1rem;
    }

    .container {
      width: 100%;
      height: 100%;
      @include display-flex(flex-start, space-between, column);

      .label {
        color: $light-red;
        font: $secondary-font;
        font-weight: 600;
      }

      .period {
        font-family: $text-font-stack;
        font-size: 14px;
        -webkit-appearance: none;
        border: 0;
        border-radius: 3px;
        text-align: left;
        background: #FFFFFF;
        position: relative;
        width: 90%;
        color: $text-extra-lightgrey;
        cursor: pointer;
        font-weight: normal;
      }

    }

    .selector {
      --header-color: #F5F5F5;
      --header-text-color: #333;
      display: inline-block;
      font-size: 1.2rem;
      position: absolute;
      width: calc(#{7 * $s} + 3em);
      text-align: left;
      background-color: #FFF;
      color: #544;
      user-select: none;
      overflow: hidden;
      box-sizing: content-box;
      border: 1px solid #EEE;
      top: 100%;
      box-shadow: 0 0 8px 1px rgba(0, 0, 0, 0.40);
      z-index: 2;
      left: 0;
      margin-top: 0.5rem;

      .year-month {
        font-size: 1.3em;
        font-weight: bold;
        color: #606575;
        position: relative;
        text-align: center;
        display: flex;
        align-items: center;
        justify-content: space-between;
        text-transform: capitalize;

        .chevron {
          padding: 2rem 2rem;
          cursor: pointer;
          opacity: 0.5;

          &:hover {
            opacity: 1;
          }

          &.right {
            margin-left: 1rem;
          }

          &.left {
            transform: rotateZ(180deg);
            margin-right: 1rem;
          }
        }
      }

      .cells {
        display: flex;
        flex-wrap: wrap;
        align-content: flex-start;
        padding: 0 1.5em;
        height: calc(#{5.5 * $s} + 6rem);
      }

      .days-of-the-week {
        padding: 0 1.5em 0;
        color: #606575;
      }

      .cell {
        position: relative;
        z-index: 1;
        font-size: 1em;
        width: $s;
        height: $s;
        margin: 0.3rem 0;
        display: inline-flex;
        justify-content: center;
        align-items: center;
        cursor: pointer;
        box-sizing: border-box;

        &::before,
        &::after {
          z-index: -1;
          content: '';
          position: absolute;
          top: 0;
          left: 0;
          width: 100%;
          height: 100%;
          transform: scale(0);
          transform-origin: center;
          transition: all 0.2s ease-out;
        }

        &.header {
          font-style: italic;
          cursor: default;
          text-transform: capitalize;
          color: inherit;
          font-size: 0.8em;
          margin: 0;
        }

        &.selected {
          color: #FFF;

          &::after {
            transform: scale(0.8);
            background-color: #DA4453;
            border-radius: 50%;
          }
        }

        &.disabled {
          color: #DDD;
          pointer-events: none;
        }

        &.empty {
          color: #CCC;
          cursor: default;
        }

        &.today {
          &::before {
            border-bottom: 2px solid #DA4453;
          }

          &:not(.highlighted1):not(.highlighted2)::before {
            transform: scaleX(0.2) scaleY(1);
          }
        }
      }

      .today-anchor {
        position: absolute;
        bottom: 1em;
        right: 1em;
        width: 1rem;
        height: 1rem;
        border-radius: 50%;
        background-color: currentColor;
        cursor: pointer;
        box-shadow: inset 0 0 0 2px #FFF;
        border: 2px solid;
      }
    }
  }
</style>
